/**
 * "Something went wrong" after a deploy, on a page that is already fixed.
 *
 * Owner, 2026-09-24: the Journal Entries search still crashed after the fix
 * was deployed. The live bundle had the fix - a test typing into the page
 * passes against it and fails against the old one - but a tab opened BEFORE
 * the deploy keeps running the old code until it is reloaded, and the error
 * screen gave no hint that reloading was the whole answer. Every user hits
 * this after every deploy.
 *
 * The deploy writes /build-info.json ({ sha, builtAt }). The page records which
 * build it started on; when anything crashes, it asks the server which build
 * is live now. Different build -> reload, once. A failed lazy-loaded chunk
 * (the deploy replaced the file it wanted) -> reload, once. A sessionStorage
 * guard stops a genuine bug from reloading in a loop.
 */
let loadedSha: string | null = null;

async function liveSha(): Promise<string | null> {
  try {
    const res = await fetch('/build-info.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const info = await res.json();
    return typeof info?.sha === 'string' ? info.sha : null;
  } catch {
    return null;
  }
}

/** Call once at start-up: remember which build this page is running. */
export async function recordLoadedBuild(): Promise<void> {
  loadedSha = await liveSha();
}

export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null;
  return /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module/i
    .test(`${e?.name || ''} ${e?.message || ''}`);
}

/**
 * If this crash is because the page is out of date, reload (once) and return
 * true. Otherwise return false and let the error screen show.
 */
export async function reloadIfStale(error: unknown): Promise<boolean> {
  const chunk = isChunkLoadError(error);
  const current = await liveSha();
  const outdated = !!(loadedSha && current && current !== loadedSha);
  if (!chunk && !outdated) return false;

  const key = `reloaded-for-build-${current || 'chunk'}`;
  try {
    if (sessionStorage.getItem(key)) return false; // already tried: a real bug, not a stale page
    sessionStorage.setItem(key, '1');
  } catch {
    // storage blocked: reload anyway, the build check still stops a loop next time
  }
  window.location.reload();
  return true;
}
